import { FailedFetchError, retryOnException } from '@atlaskit/frontend-utilities/retry-operation';
import { fg } from '@atlaskit/platform-feature-flags';
import { captureException } from '@atlassian/error-handling';

// 5xx HTTP errors that should be retried
class RetryableHttpError extends FailedFetchError {
	public status?: number;

	constructor(message: string, status?: number) {
		super({
			error: new Error(message),
			path: '',
			method: 'GET',
		});
		this.name = 'RetryableHttpError';
		this.status = status;
	}
}

// Network errors that should be retried
class RetryableNetworkError extends FailedFetchError {
	constructor(message: string) {
		super({
			error: new Error(message),
			path: '',
			method: 'GET',
		});
		this.name = 'RetryableNetworkError';
	}
}

// 3 minutes (36 intervals of 5 seconds)
const PROVISIONING_RETRY_INTERVALS = Array(36).fill(5000);

import type {
	ProvisionPlanRequest,
	ProvisionPlanResponse,
	ProvisionStartAndTrackResquest,
	ProvisionStartRequest,
	ProvisionStartResponse,
	ProvisionStatusRequest,
	ProvisionStatusResponse,
	TrackProvisionJobRequest,
	TrackProvisionJobResponse,
} from './types';
import { poll } from './utils';

const XFLOW_SERVICE_URL = '/gateway/api/xflow/provision/cloud';

const STOP_PERCENTAGE = 0.9;
const PROGRESS_INCREMENT = 1 / 50;

export const planProvisioning = async ({
	cloudId,
	sourceComponent,
	sourceContext,
}: ProvisionPlanRequest): Promise<ProvisionPlanResponse> => {
	const response = await fetch(
		`${XFLOW_SERVICE_URL}/${encodeURIComponent(cloudId)}/product/teamwork-collection/job/core/plan`,
		{
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				sourceComponent,
				sourceContext,
			}),
		},
	);

	if (!response.ok) {
		throw new Error(
			`Non-OK response when fetching plan for TWC XFlow provisioning: HTTP ${response.status}: ${response.statusText}`,
		);
	}

	return response.json();
};

const startProvisioning = async ({
	cloudId,
	signal,
	locale,
	sourceComponent,
	sourceContext,
}: ProvisionStartRequest): Promise<ProvisionStartResponse> => {
	try {
		const response = await fetch(
			`${XFLOW_SERVICE_URL}/${encodeURIComponent(cloudId)}/product/teamwork-collection/job/core/start`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					sourceComponent,
					sourceContext,
					locale,
				}),
				signal,
			},
		);

		if (!response.ok) {
			throw new Error(
				`Non-OK response when starting TWC XFlow provisioning: HTTP ${response.status}: ${response.statusText}`,
			);
		}

		return response.json();
	} catch (error) {
		captureException('Failed to start TWC XFlow provisioning', error as Error, {
			cloudId,
			packageName: '@atlassian/twc-xflow-modal',
			teamName: 'starburst',
		});
		throw error;
	}
};

const checkStatus = async ({
	cloudId,
}: ProvisionStatusRequest): Promise<ProvisionStatusResponse> => {
	const isRetryEnabled = fg('twc_provisioning_retry_gate');

	try {
		const response = await fetch(
			`${XFLOW_SERVICE_URL}/${encodeURIComponent(cloudId)}/product/teamwork-collection/job/core/status`,
			{
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
				},
			},
		);

		if (isRetryEnabled && !response.ok) {
			if (response.status >= 500) {
				throw new RetryableHttpError(
					`HTTP ${response.status}: ${response.statusText}`,
					response.status,
				);
			}
			throw new Error(`HTTP ${response.status}: ${response.statusText}`);
		}

		return response.json();
	} catch (error) {
		captureException('Failed to check the status of TWC XFlow provisioning', error as Error, {
			cloudId,
			packageName: '@atlassian/twc-xflow-modal',
			teamName: 'starburst',
		});

		if (isRetryEnabled && error instanceof TypeError && error.message.includes('fetch')) {
			throw new RetryableNetworkError(`Network error: ${error.message}`);
		}

		throw error;
	}
};

const trackProvisionJob = async ({
	cloudId,
	tasks,
	callback,
	pollingOptions,
	initialStatus,
}: TrackProvisionJobRequest): Promise<TrackProvisionJobResponse> => {
	try {
		let statusResponse = initialStatus;
		let completedTasks = new Set();
		let lastSeenTask = initialStatus.step;
		let progress = 0;

		await poll(
			async () => {
				if (fg('twc_provisioning_retry_gate')) {
					statusResponse = await retryOnException(() => checkStatus({ cloudId }), {
						intervalsMS: PROVISIONING_RETRY_INTERVALS,
						retryOn: (error) =>
							error instanceof RetryableHttpError || error instanceof RetryableNetworkError,
					});
				} else {
					statusResponse = await checkStatus({ cloudId });
				}

				let taskCompleted = false;
				if (statusResponse.step !== lastSeenTask) {
					lastSeenTask = statusResponse.step;
					completedTasks.add(lastSeenTask);
					taskCompleted = true;
				}

				// Handle Provision status: https://hello.atlassian.net/wiki/spaces/DISC/pages/3922151447/Xflow+Provisioning+API+-+Concept#Available-Status
				switch (statusResponse.status) {
					case 'PROVISIONING_IN_PROGRESS':
						const currentTaskPercentage = completedTasks.size / tasks.length;
						const nextTaskPercentage = (completedTasks.size + 1) / tasks.length;

						if (taskCompleted) {
							progress = currentTaskPercentage;
						} else if (progress < STOP_PERCENTAGE && progress < nextTaskPercentage) {
							progress += PROGRESS_INCREMENT;
						}

						callback(progress, statusResponse);
						return true;
					case 'PROVISIONING_COMPLETED':
						// We are done clear completed tasks
						completedTasks.clear();
						callback(1, statusResponse);
						return false;
					case 'PROVISIONING_ABORTED':
						throw new Error('Provisioning aborted');
					case 'PROVISIONING_FAILED':
						throw new Error('Provisioning failed');
					default:
						throw new Error(`Unknown status found: ${statusResponse.status} `);
				}
			},
			// Continue until provisioning is on end state
			(response) => response,
			pollingOptions,
		);

		return {
			...statusResponse,
			progress: 1,
		};
	} catch (error) {
		captureException('Error when tracking provisioning status for job', error as Error, {
			cloudId,
			packageName: '@atlassian/twc-xflow-modal',
			teamName: 'starburst',
		});

		throw error;
	}
};

export const startAndTrackProvisioning = async ({
	cloudId,
	callback,
	pollingOptions,
	initialStatus,
	locale,
	sourceComponent,
	sourceContext,
}: ProvisionStartAndTrackResquest): Promise<TrackProvisionJobResponse> => {
	try {
		let startResponse;
		if (!initialStatus) {
			startResponse = await startProvisioning({
				cloudId,
				signal: pollingOptions.signal,
				locale,
				sourceComponent,
				sourceContext,
			});
		}
		const statusResponse = await checkStatus({ cloudId });
		switch (statusResponse.status) {
			default:
			case 'PROVISIONING_IN_PROGRESS':
				return trackProvisionJob({
					cloudId,
					tasks: startResponse?.tasks ?? [{ name: 'dummy-task' }],
					callback,
					pollingOptions,
					initialStatus: statusResponse,
				});
			case 'PROVISIONING_ABORTED':
				throw new Error('Provisioning aborted');
			case 'PROVISIONING_FAILED':
				throw new Error('Provisioning failed');
			case 'PROVISIONING_COMPLETED':
				return {
					...statusResponse,
					progress: 1,
				};
		}
	} catch (error) {
		captureException('Error when starting and tracking provisioning', error as Error, {
			cloudId,
			packageName: '@atlassian/twc-xflow-modal',
			teamName: 'starburst',
		});
		throw error;
	}
};
